import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { CREATE_TASKS, UPDATE_TASKS } from 'services/api/task';
import { useAccessToken } from 'providers/AccessTokenProvider';
import { TaskModalProps, FormValues } from '../types/types';
import toast from 'react-hot-toast';
import '../styles/task.css';
import TaskFormFields from './TaskFormFields';
import {
  buildStageReminderPayload,
  stageReminderToFormValue
} from '../utils/stageReminder';
import { debounce } from 'lodash';
import { JobOption } from '../types/formFields';
import { useGlobalSocket } from 'hooks/useGlobalSocket';
import { useNotificationSocket } from 'hooks/useNotificationSocket';

const TaskModal = ({
  show,
  handleClose,
  task,
  employees = [],
  jobs = []
}: TaskModalProps) => {
  const { authUser } = useAccessToken();
  const isEditMode = !!task;
  const queryClient = useQueryClient();
  const [jobSearchTerm, setJobSearchTerm] = useState<string>('');
  const [filteredJobs, setFilteredJobs] = useState<JobOption[]>(jobs);

  // Global socket for task events
  useGlobalSocket({
    enabled: Boolean(authUser),
    onTaskCreated: taskData => {
      console.log('Task created via socket:', taskData);
      // Invalidate relevant queries to refresh UI
      queryClient.invalidateQueries(['tasks']);
      queryClient.invalidateQueries(['filteredTasks']);
      if (taskData.jobId) {
        queryClient.invalidateQueries(['jobTasks', taskData.jobId]);
      }
    },
    onTaskUpdated: taskData => {
      console.log('Task updated via socket:', taskData);
      // Invalidate relevant queries to refresh UI
      queryClient.invalidateQueries(['tasks']);
      queryClient.invalidateQueries(['filteredTasks']);
      if (taskData.jobId) {
        queryClient.invalidateQueries(['jobTasks', taskData.jobId]);
      }
    }
  });

  // Notification socket for real-time notification updates
  useNotificationSocket({
    enabled: Boolean(authUser),
    onNewNotification: notification => {
      console.log('New notification received in TaskModal:', notification);
      queryClient.invalidateQueries(['notifications']);
    },
    onNotificationCountUpdate: data => {
      queryClient.setQueryData(['notificationCount'], () => ({
        data: { count: data.count }
      }));
    }
  });

  const sortJobs = useCallback((jobList: JobOption[]) => {
    return [...jobList].sort((a, b) => {
      return a.label.localeCompare(b.label);
    });
  }, []);

  useEffect(() => {
    setFilteredJobs(sortJobs(jobs));
  }, [jobs, sortJobs]);

  const debouncedJobSearch = useCallback(
    debounce((searchTerm: string) => {
      if (!searchTerm.trim()) {
        setFilteredJobs(sortJobs(jobs));
        return;
      }

      const query = searchTerm.toLowerCase();
      const filtered = jobs.filter(job => {
        const name = job.label.toLowerCase();
        return name.includes(query);
      });

      setFilteredJobs(sortJobs(filtered));
    }, 300),
    [jobs, sortJobs]
  );

  const handleJobSearch = (inputValue: string) => {
    setJobSearchTerm(inputValue);
    debouncedJobSearch(inputValue);
  };

  const form = useForm<FormValues>({
    defaultValues: {
      task: '',
      description: '',
      subTask: [],
      date: null,
      time: '',
      assignedTo: [],
      completed: false,
      priority: 'medium',
      status: 'not started',
      jobId: '',
      creator: '',
      comments: [],
      stageReminder: { triggerType: 'none', stageIds: [] }
    }
  });

  const { reset, handleSubmit } = form;

  // Track previous value of show
  const prevShowRef = useRef<boolean>(false);

  useEffect(() => {
    // Only reset when modal is opened (show transitions from false to true) or when task changes while open
    if (show && (!prevShowRef.current || task)) {
      if (task) {
        try {
          const assignedUsers = task.assignedTo.map((user: any) => {
            if (typeof user === 'object' && user !== null) {
              return {
                value: user.id || user._id || String(user),
                label: user.firstName
                  ? `${user.firstName} ${user.lastName || ''}`.trim()
                  : user.email || String(user.id || user._id || user)
              };
            }
            const employee = employees.find(emp => emp.value === user);
            return employee || { value: String(user), label: String(user) };
          });

          let jobId = '';
          if ((task as any).job) {
            jobId = (task as any).job.id || (task as any).job._id || '';
          } else if (task.jobId) {
            jobId = String(task.jobId);
          }

          let creatorId = '';
          if (task.creator) {
            if (typeof task.creator === 'object' && task.creator !== null) {
              creatorId =
                (task.creator as any).id || (task.creator as any)._id || '';
            } else {
              creatorId = String(task.creator);
            }
          } else {
            creatorId = (authUser as any)?.id || authUser?._id || '';
          }

          reset({
            task: task.task,
            description: task.description || '',
            subTask: task.subTask || [],
            date: new Date(task.date),
            time: task.time,
            assignedTo: assignedUsers,
            completed: task.completed,
            priority: task.priority,
            status: task.status,
            jobId: jobId,
            creator: creatorId,
            comments: task.comments || [],
            stageReminder: stageReminderToFormValue(task.stageReminder)
          });
        } catch (error) {
          console.error('Error setting form values:', error);
          toast.error('Error loading task data');
        }
      } else {
        const defaultJobId = jobs && jobs.length === 1 ? jobs[0].value : '';
        reset({
          task: '',
          description: '',
          subTask: [],
          date: null,
          time: '',
          assignedTo: [],
          completed: false,
          priority: 'medium',
          status: 'not started',
          jobId: defaultJobId,
          creator: (authUser as any)?.id || authUser?._id || '',
          comments: [],
          stageReminder: { triggerType: 'none', stageIds: [] }
        });
      }
    }
    prevShowRef.current = show;
  }, [show, task]);

  const createTaskMutation = useMutation(CREATE_TASKS, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notificationCount']);

      if (jobs && jobs.length > 0) {
        const jobId = jobs[0].value;
        queryClient.invalidateQueries(['jobTasks', jobId]);
        queryClient.invalidateQueries(['filteredTasks', { jobId }]);
      }

      handleClose();
      reset();
      toast.success('Task created successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.messages || 'Failed to create task');
    }
  });

  const updateTaskMutation = useMutation(
    (data: { taskId: string; data: any }) => UPDATE_TASKS(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tasks']);
        queryClient.invalidateQueries(['filteredTasks']);
        queryClient.invalidateQueries(['activityLogs']);
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notificationCount']);

        const taskId = (task as any)?.id || task?._id;
        if (taskId) {
          queryClient.invalidateQueries(['task', taskId]);
          queryClient.invalidateQueries(['activityLogs', taskId]);
        }

        if (jobs && jobs.length > 0) {
          const jobId = jobs[0].value;
          queryClient.invalidateQueries(['jobTasks', jobId]);
          queryClient.invalidateQueries(['filteredTasks', { jobId }]);
        }

        handleClose();
        reset();
        toast.success('Task updated successfully');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.messages || 'Failed to update task');
      }
    }
  );

  const onSubmit = (data: FormValues) => {
    try {
      let creatorId = '';
      if (data.creator) {
        if (typeof data.creator === 'object' && data.creator !== null) {
          creatorId =
            (data.creator as any).id || (data.creator as any)._id || '';
        } else {
          creatorId = String(data.creator);
        }
      } else {
        creatorId = (authUser as any)?.id || authUser?._id || '';
      }

      const payload = {
        task: data.task,
        description: data.description,
        subTask: data.subTask.map(subtask => ({
          text: subtask.text,
          completed: subtask.completed,
          completedAt: subtask.completedAt,
          completedBy: subtask.completedBy
        })),
        date: data.date ? data.date.toISOString().split('T')[0] : null,
        time: data.time || null,
        assignedTo: data.assignedTo.map(user => user.value),
        completed: data.completed,
        priority: data.priority,
        status: data.status,
        creator: creatorId,
        jobId: data.jobId,
        comments:
          data.comments?.map(comment => ({
            text: comment.text,
            userId: comment.userId,
            createdAt: comment.createdAt,
            editedAt: comment.editedAt
          })) || [],
        // Stage-based reminder only applies to job-associated tasks.
        // null clears any existing reminder on update; ignored on create.
        stageReminder: buildStageReminderPayload(data.jobId, data.stageReminder)
      };

      if (isEditMode && task) {
        updateTaskMutation.mutate({
          taskId: (task as any).id || task._id,
          data: payload
        });
      } else {
        createTaskMutation.mutate(payload);
      }
    } catch (error) {
      toast.error(
        (error as any).response?.data?.messages ||
          'An error occurred while submitting the form'
      );
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      className="task-modal-elevated"
      backdropClassName="task-modal-elevated-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? 'Edit Task' : 'Add New Task'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <TaskFormFields
            form={form}
            employees={employees}
            jobs={filteredJobs}
            onJobSearch={handleJobSearch}
            jobSearchTerm={jobSearchTerm}
            isEditMode={isEditMode}
            taskId={(task as any)?.id || task?._id}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={
              createTaskMutation.isLoading || updateTaskMutation.isLoading
            }
          >
            {createTaskMutation.isLoading || updateTaskMutation.isLoading
              ? 'Saving...'
              : isEditMode
              ? 'Update Task'
              : 'Create Task'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TaskModal;
